import { useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/firebase";

type CompetitorProduct = { id: string; name?: string; companyId?: string };
type ScrapeTarget = {
  id: string;
  companyName: string;
  listPageUrl: string;
  crawlIntervalDays: number;
  scheduleTimeJst: string;
  renderMode: "static" | "headless_browser";
  isActive: boolean;
  targetKeyword?: string;
  targetQualifications?: string[];
  qualificationMappings?: { qualificationName: string; competitorProductId: string; selectedPriceHint?: string }[];
  selectors?: { item?: string; name?: string; price?: string };
  lastSuccessAt?: { seconds?: number } | null;
  lastFailureAt?: { seconds?: number } | null;
};

type ExtractionResult = { qualificationName: string; matchedCount: number; prices: string[] };
type TestResult = {
  success: boolean;
  message: string;
  extractionResults?: ExtractionResult[];
};

const initialForm = {
  companyName: "",
  listPageUrl: "",
  crawlIntervalDays: 3,
  scheduleTimeJst: "09:00",
  renderMode: "static" as "static" | "headless_browser",
  isActive: true,
  targetKeyword: "",
  targetQualificationsText: "",
  selectorItem: ".p-categoryDetailItem",
  selectorName: "dt.p-categoryDetailItem__title",
  selectorPrice: "",
};

const parseQualifications = (value: string) => value.split(/\n|,/).map((v) => v.trim()).filter(Boolean);
const formatTs = (v?: { seconds?: number } | null) => (v?.seconds ? new Date(v.seconds * 1000).toLocaleString("ja-JP") : "-");

export default function CompetitiveScrapePage() {
  const manageFn = httpsCallable(functions, "manageCompetitorScrapeTargets");

  const [targets, setTargets] = useState<ScrapeTarget[]>([]);
  const [products, setProducts] = useState<CompetitorProduct[]>([]);
  const [form, setForm] = useState(initialForm);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingTargetId, setTestingTargetId] = useState<string | null>(null);
  const [testResultMap, setTestResultMap] = useState<Record<string, TestResult>>({});
  const [mappingDraft, setMappingDraft] = useState<Record<string, Record<string, { competitorProductId: string; selectedPriceHint: string }>>>({});

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [targetRes, productRes] = await Promise.all([
        manageFn({ action: "list" }),
        manageFn({ action: "listProducts" }),
      ]);
      const targetItems = (targetRes.data as { items?: ScrapeTarget[] }).items || [];
      const productItems = (productRes.data as { items?: CompetitorProduct[] }).items || [];
      setTargets(targetItems);
      setProducts(productItems);
    } catch (error) {
      console.error(error);
      setMessage("データ読み込みに失敗しました。Functionsを再デプロイしてください。");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const activeCount = useMemo(() => targets.filter((t) => t.isActive).length, [targets]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.companyName || !form.listPageUrl) return setMessage("企業名とURLは必須です。");
    setSaving(true);
    setMessage("");
    try {
      await manageFn({
        action: "create",
        payload: {
          companyName: form.companyName.trim(),
          listPageUrl: form.listPageUrl.trim(),
          crawlIntervalDays: form.crawlIntervalDays,
          scheduleTimeJst: form.scheduleTimeJst,
          renderMode: form.renderMode,
          isActive: form.isActive,
          targetKeyword: form.targetKeyword.trim(),
          targetQualifications: parseQualifications(form.targetQualificationsText),
          selectors: {
            item: form.selectorItem.trim(),
            name: form.selectorName.trim(),
            price: form.selectorPrice.trim(),
          },
        },
      });
      setForm(initialForm);
      setMessage("登録しました。次は下の「テスト取得」→「マッピング保存」を実行してください。");
      await fetchAll();
    } catch (error: any) {
      console.error(error);
      setMessage(error?.message || "登録に失敗しました。");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (target: ScrapeTarget) => {
    setTestingTargetId(target.id);
    try {
      const fn = httpsCallable(functions, "testCompetitorScrape");
      const res = await fn({
        targetId: target.id,
        listPageUrl: target.listPageUrl,
        targetKeyword: target.targetKeyword || "",
        targetQualifications: target.targetQualifications || [],
        qualificationMappings: target.qualificationMappings || [],
        selectors: target.selectors || {},
      });
      const data = res.data as TestResult;
      setTestResultMap((prev) => ({ ...prev, [target.id]: data }));

      const initDraft: Record<string, { competitorProductId: string; selectedPriceHint: string }> = {};
      (data.extractionResults || []).forEach((r) => {
        const existing = target.qualificationMappings?.find((m) => m.qualificationName === r.qualificationName);
        initDraft[r.qualificationName] = {
          competitorProductId: existing?.competitorProductId || "",
          selectedPriceHint: existing?.selectedPriceHint || r.prices[0] || "",
        };
      });
      setMappingDraft((prev) => ({ ...prev, [target.id]: initDraft }));
    } catch (error: any) {
      setTestResultMap((prev) => ({ ...prev, [target.id]: { success: false, message: error?.message || "テスト失敗" } }));
    } finally {
      setTestingTargetId(null);
    }
  };

  const saveMapping = async (targetId: string) => {
    const draft = mappingDraft[targetId] || {};
    const qualificationMappings = Object.entries(draft)
      .filter(([, v]) => v.competitorProductId)
      .map(([qualificationName, v]) => ({ qualificationName, competitorProductId: v.competitorProductId, selectedPriceHint: v.selectedPriceHint }));

    await manageFn({ action: "updateMappings", targetId, qualificationMappings });
    setMessage("マッピングを保存しました。");
    await fetchAll();
  };

  const toggleTarget = async (target: ScrapeTarget) => {
    try {
      await manageFn({ action: "toggle", targetId: target.id, isActive: !target.isActive });
      setMessage(`「${target.companyName}」を${target.isActive ? "無効" : "有効"}化しました。`);
      await fetchAll();
    } catch (error: any) {
      setMessage(error?.message || "有効状態の更新に失敗しました。");
    }
  };

  const deleteTarget = async (target: ScrapeTarget) => {
    if (!window.confirm(`「${target.companyName}」を削除しますか？`)) return;
    try {
      await manageFn({ action: "delete", targetId: target.id });
      setMessage("ターゲットを削除しました。");
      await fetchAll();
    } catch (error: any) {
      setMessage(error?.message || "削除に失敗しました。");
    }
  };

  const statusLabel = (target: ScrapeTarget) => {
    if (!target.isActive) return { text: "停止中", className: "bg-gray-100 text-gray-700" };
    const success = target.lastSuccessAt?.seconds || 0;
    const fail = target.lastFailureAt?.seconds || 0;
    if (success === 0 && fail === 0) return { text: "未実行", className: "bg-slate-100 text-slate-700" };
    if (fail > success) return { text: "直近失敗", className: "bg-red-100 text-red-700" };
    return { text: "稼働中", className: "bg-emerald-100 text-emerald-700" };
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">競合情報取得設定</h1>
        <p className="text-sm text-gray-500 mt-1">手順: ①URL登録 → ②テスト取得 → ③資格ごとに価格をマッピング保存。</p>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-xl border bg-white p-4"><p className="text-xs text-gray-500">登録URL数</p><p className="text-2xl font-semibold">{targets.length}</p></div>
        <div className="rounded-xl border bg-white p-4"><p className="text-xs text-gray-500">有効ターゲット</p><p className="text-2xl font-semibold text-emerald-600">{activeCount}</p></div>
        <div className="rounded-xl border bg-white p-4"><p className="text-xs text-gray-500">ステータス</p><p className="text-sm mt-2">{message || "-"}</p></div>
      </section>

      <section className="rounded-xl border bg-white p-4 md:p-6">
        <h2 className="font-semibold">① URL登録</h2>
        <form onSubmit={handleRegister} className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="text-sm">競合企業名<input className="mt-1 w-full border rounded px-3 py-2" value={form.companyName} onChange={(e)=>setForm((p)=>({...p,companyName:e.target.value}))} /></label>
          <label className="text-sm">取得URL<input className="mt-1 w-full border rounded px-3 py-2" value={form.listPageUrl} onChange={(e)=>setForm((p)=>({...p,listPageUrl:e.target.value}))} /></label>
          <label className="text-sm">対象資格名（複数可）<textarea className="mt-1 w-full border rounded px-3 py-2 h-24" placeholder={"行政書士\n宅建士"} value={form.targetQualificationsText} onChange={(e)=>setForm((p)=>({...p,targetQualificationsText:e.target.value}))} /></label>
          <label className="text-sm">補助キーワード<input className="mt-1 w-full border rounded px-3 py-2" value={form.targetKeyword} onChange={(e)=>setForm((p)=>({...p,targetKeyword:e.target.value}))} /></label>
          <label className="text-sm">カードセレクタ<input className="mt-1 w-full border rounded px-3 py-2" value={form.selectorItem} onChange={(e)=>setForm((p)=>({...p,selectorItem:e.target.value}))} /></label>
          <label className="text-sm">名称セレクタ<input className="mt-1 w-full border rounded px-3 py-2" value={form.selectorName} onChange={(e)=>setForm((p)=>({...p,selectorName:e.target.value}))} /></label>
          <label className="text-sm">価格セレクタ（任意）<input className="mt-1 w-full border rounded px-3 py-2" value={form.selectorPrice} onChange={(e)=>setForm((p)=>({...p,selectorPrice:e.target.value}))} /></label>
          <label className="text-sm">取得間隔（日）<input type="number" min={1} className="mt-1 w-full border rounded px-3 py-2" value={form.crawlIntervalDays} onChange={(e)=>setForm((p)=>({...p,crawlIntervalDays:Number(e.target.value)||3}))} /></label>
          <label className="text-sm">実行時刻<input type="time" className="mt-1 w-full border rounded px-3 py-2" value={form.scheduleTimeJst} onChange={(e)=>setForm((p)=>({...p,scheduleTimeJst:e.target.value}))} /></label>
          <div className="md:col-span-2 flex justify-end"><button className="px-4 py-2 bg-emerald-600 text-white rounded" disabled={saving}>{saving?"保存中...":"URLを登録"}</button></div>
        </form>
      </section>

      <section className="rounded-xl border bg-white p-4 md:p-6">
        <h2 className="font-semibold">② テスト取得 → ③ データマッピング</h2>
        {loading ? <p className="text-gray-500 mt-3">読み込み中...</p> : null}
        <div className="space-y-4 mt-4">
          {targets.map((target) => {
            const result = testResultMap[target.id];
            const draft = mappingDraft[target.id] || {};
            const status = statusLabel(target);
            return (
              <div key={target.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium">{target.companyName}</p>
                    <p className="text-xs text-gray-500 break-all">{target.listPageUrl}</p>
                    <p className="text-xs text-gray-500">資格: {target.targetQualifications?.join(" / ") || target.targetKeyword || "未設定"}</p>
                    <div className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${status.className}`}>{status.text}</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button className="px-3 py-2 bg-blue-600 text-white rounded disabled:bg-gray-400" disabled={testingTargetId===target.id} onClick={()=>handleTest(target)}>{testingTargetId===target.id?"取得中...":"テスト取得"}</button>
                    <button className={`px-3 py-2 rounded text-white ${target.isActive ? "bg-amber-500" : "bg-emerald-600"}`} onClick={()=>toggleTarget(target)}>{target.isActive ? "無効化" : "有効化"}</button>
                    <button className="px-3 py-2 bg-red-600 text-white rounded" onClick={()=>deleteTarget(target)}>削除</button>
                  </div>
                </div>

                {result ? (
                  <div className="mt-3 space-y-3">
                    <p className={`text-sm ${result.success ? "text-emerald-700" : "text-red-700"}`}>{result.message}</p>
                    {(result.extractionResults || []).map((r) => (
                      <div key={r.qualificationName} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end border rounded p-2">
                        <div>
                          <p className="text-xs text-gray-500">資格名</p>
                          <p className="text-sm font-medium">{r.qualificationName}</p>
                          <p className="text-xs text-gray-500">ヒット: {r.matchedCount}</p>
                        </div>
                        <label className="text-xs">価格候補
                          <select className="mt-1 w-full border rounded px-2 py-1" value={draft[r.qualificationName]?.selectedPriceHint || ""} onChange={(e)=>setMappingDraft((prev)=>({
                            ...prev,
                            [target.id]: { ...(prev[target.id]||{}), [r.qualificationName]: { ...(prev[target.id]?.[r.qualificationName]||{competitorProductId:"",selectedPriceHint:""}), selectedPriceHint:e.target.value } }
                          }))}>
                            <option value="">選択してください</option>
                            {r.prices.map((p)=><option key={p} value={p}>{p}</option>)}
                          </select>
                        </label>
                        <label className="text-xs md:col-span-2">競合商品へマッピング
                          <select className="mt-1 w-full border rounded px-2 py-1" value={draft[r.qualificationName]?.competitorProductId || ""} onChange={(e)=>setMappingDraft((prev)=>({
                            ...prev,
                            [target.id]: { ...(prev[target.id]||{}), [r.qualificationName]: { ...(prev[target.id]?.[r.qualificationName]||{selectedPriceHint:"",competitorProductId:""}), competitorProductId:e.target.value } }
                          }))}>
                            <option value="">選択してください</option>
                            {products.map((p)=><option key={p.id} value={p.id}>{p.name || p.id}</option>)}
                          </select>
                        </label>
                      </div>
                    ))}
                    {(result.extractionResults || []).length > 0 ? (
                      <div className="flex justify-end"><button className="px-3 py-2 bg-emerald-600 text-white rounded" onClick={()=>saveMapping(target.id)}>マッピング保存</button></div>
                    ) : null}
                  </div>
                ) : null}

                <div className="mt-3 text-xs text-gray-500">最終成功: {formatTs(target.lastSuccessAt)} / 最終失敗: {formatTs(target.lastFailureAt)}</div>
              </div>
            );
          })}
          {!loading && targets.length===0 ? <p className="text-gray-500 text-sm">まだターゲットがありません。</p> : null}
        </div>
      </section>
    </div>
  );
}
